import { Search as SearchIcon, X } from "lucide-react";
import { usePrimaryColor } from "./PrimaryColorContext";


/**
 * A search bar component that allows a user to type in a search value
 * and call a function to set the search value when the user types.
 *
 * The component will also display a cancel button when the user has typed
 * something in, and will clear the search value if the cancel button is
 * clicked.
 *
 * Uses lucide-react icons and CSS variable-driven theming.
 *
 * @param {string} search The current search value
 * @param {function} setSearch A function to set the search value
 * @param {string} [type=text] The type of the input field
 * @param {string} [placeholder=Search...] The placeholder text
 * @param {string} [className] Additional CSS classes to apply
 * @author Forge
 * @date 2026-04-07
 * @version 4.0
 */
export default function Search(props) {
  const { primaryColor } = usePrimaryColor();
  const { search, setSearch, type = "text", placeholder = "Search...", className = "" } = props;
  let newProps = { ...props };
  delete newProps.search;
  delete newProps.setSearch;
  delete newProps.type;
  delete newProps.placeholder;
  delete newProps.className;

  return (
    <div className="relative group w-full">
      <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none" style={{ paddingLeft: '14px' }}>
        <SearchIcon
          size={16}
          className={`text-gray-400 group-focus-within:text-${primaryColor}-800 transition-colors duration-150`}
        />
      </div>
      <input
        type={type}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-[#f8fafc] border border-gray-200 rounded-[10px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-${primaryColor}-800 focus:ring-[3px] focus:ring-[rgba(0,0,0,0.04)] transition-all duration-150 hover:bg-white hover:border-gray-300 ${className}`}
        style={{
          paddingLeft: '42px',
          paddingRight: search !== "" ? '36px' : '12px',
          paddingTop: '0',
          paddingBottom: '0',
          height: '40px',
          fontSize: '13px',
          lineHeight: '40px'
        }}
        {...newProps}
      />
      {search !== "" && (
        <button
          onClick={() => setSearch("")}          className={`absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-${primaryColor}-800`}
          aria-label="Clear search"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
